import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { BackButton, DataGrid, KeenIcon } from '@/components';
import { Container } from '@/components/container';
import usePagination from '@/hooks/usePagination';
import { useGetMediationCasesQuery } from '@/services/MediationService';
import {
  BerthMediationCase,
  BerthMediationStatus,
} from '@/types/berth-mediation.types';
import { formatDisplayDate } from '@/utils/Date';

import {
  BERTH_CONFLICT_STATUSES,
  BERTH_CONFLICTS_PATH,
  getBerthConflictCasePath,
} from './bookingDrillDowns';

const STATUS_LABELS: Record<BerthMediationStatus, string> = {
  OPEN: 'Open',
  AWAITING_BOATER_B: 'Awaiting boater',
  AWAITING_MANAGER: 'Awaiting manager',
  AWAITING_BOATER_A: 'Awaiting requesting boater',
  RESOLVED: 'Resolved',
  CANCELLED: 'Cancelled',
  EXPIRED: 'Expired',
};

const getBoaterName = (booking?: BerthMediationCase['requesting_booking']) =>
  booking?.user?.name ||
  booking?.boater_name ||
  booking?.user?.email ||
  booking?.boater_email ||
  '-';

const BerthConflictListings = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    pagination,
    setPagination,
  } = usePagination();

  const { data, isLoading, isFetching, isError, error } =
    useGetMediationCasesQuery({
      status: BERTH_CONFLICT_STATUSES,
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
    });

  useEffect(() => {
    if (data) {
      handleTotalPages(data.total_pages || 1);
      handleTotalItems(data.total || 0);
    }
  }, [data, handleTotalPages, handleTotalItems]);

  useEffect(() => {
    if (isError) {
      const apiErr = error as { data?: { message?: string } };
      enqueueSnackbar(
        apiErr?.data?.message ?? 'Failed to load berth conflicts',
        { variant: 'error' }
      );
    }
  }, [isError, error, enqueueSnackbar]);

  const columns = useMemo(
    () => [
      {
        accessor: (row: BerthMediationCase) => row.harbor?.name,
        id: 'harbor',
        header: 'Harbor',
        enableSorting: false,
        cell: ({ row }: { row: { original: BerthMediationCase } }) => (
          <span className="text-sm font-medium text-gray-900">
            {row.original.harbor?.name || '-'}
          </span>
        ),
        meta: { className: 'min-w-[160px]' },
      },
      {
        accessor: (row: BerthMediationCase) => row.original_berth?.berth_number,
        id: 'berth',
        header: 'Berth',
        enableSorting: false,
        cell: ({ row }: { row: { original: BerthMediationCase } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.original_berth?.berth_number ||
              row.original.conflicting_booking?.berth?.berth_number ||
              '-'}
          </span>
        ),
        meta: { className: 'min-w-[100px]' },
      },
      {
        accessor: (row: BerthMediationCase) =>
          getBoaterName(row.requesting_booking),
        id: 'requesting',
        header: 'Requesting boater',
        enableSorting: false,
        cell: ({ row }: { row: { original: BerthMediationCase } }) => (
          <span className="text-gray-800 font-normal">
            {getBoaterName(row.original.requesting_booking)}
          </span>
        ),
        meta: { className: 'min-w-[160px]' },
      },
      {
        accessor: (row: BerthMediationCase) =>
          getBoaterName(row.conflicting_booking),
        id: 'conflicting',
        header: 'Conflicting boater',
        enableSorting: false,
        cell: ({ row }: { row: { original: BerthMediationCase } }) => (
          <span className="text-gray-800 font-normal">
            {getBoaterName(row.original.conflicting_booking)}
          </span>
        ),
        meta: { className: 'min-w-[160px]' },
      },
      {
        accessor: (row: BerthMediationCase) => row.overlap_start,
        id: 'overlap',
        header: 'Overlap',
        enableSorting: false,
        cell: ({ row }: { row: { original: BerthMediationCase } }) => (
          <span className="text-gray-800 font-normal">
            {formatDisplayDate(row.original.overlap_start)} -{' '}
            {formatDisplayDate(row.original.overlap_end)}
          </span>
        ),
        meta: { className: 'min-w-[190px]' },
      },
      {
        accessor: (row: BerthMediationCase) => row.status,
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: BerthMediationCase } }) => (
          <span className="badge badge-sm badge-warning">
            {STATUS_LABELS[row.original.status] ?? row.original.status}
          </span>
        ),
        meta: { className: 'min-w-[160px]' },
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        enableSorting: false,
        cell: ({ row }: { row: { original: BerthMediationCase } }) => {
          const bookingId =
            row.original.requesting_booking_id ??
            row.original.conflicting_booking_id;

          return (
            <button
              type="button"
              className="btn btn-xs btn-light"
              disabled={!bookingId}
              onClick={() =>
                bookingId &&
                navigate(getBerthConflictCasePath(bookingId, row.original.id), {
                  state: { from: BERTH_CONFLICTS_PATH },
                })
              }
            >
              View
            </button>
          );
        },
        meta: { className: 'min-w-[80px]' },
      },
    ],
    [navigate]
  );

  const cases = data?.data ?? [];

  return (
    <Container>
      <div className="berth-listings card card-grid w-full">
        <div className="px-6 pt-5">
          {/* This list is only opened from the dashboard's Berth Conflicts card. */}
          <div className="flex items-center gap-2">
            <BackButton customBackPath="/" label="Back to dashboard" />
            <h1 className="text-base font-semibold text-gray-900">
              Berth Conflicts
            </h1>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Extension mediation cases that need action
          </p>
        </div>
        <div className="card-body">
          {isLoading || isFetching ? (
            <div className="text-center py-4" role="status" aria-live="polite">
              <KeenIcon
                type="filled"
                icon="spinner"
                className="text-gray-500 text-2xl animate-spin"
              />
              <span className="ml-2 text-gray-700">Loading...</span>
            </div>
          ) : isError ? (
            <div className="text-center py-4 text-red-600" role="alert">
              Error loading data. Please try again.
            </div>
          ) : cases.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-gray-500">No berth conflicts need action.</p>
            </div>
          ) : (
            <div className="berth-grid-wrapper">
              <DataGrid
                data-testid="berth-conflicts-data-grid"
                columns={columns}
                data={cases}
                rowSelect={false}
                loading={isLoading || isFetching}
                pagination={{
                  page: pagination.pageIndex,
                  size: pagination.pageSize,
                  totalItems: data?.total || totalItems,
                  totalPages: data?.total_pages || totalPages,
                  setPagination,
                  info: `{from} - {to} of {count}`,
                  sizes: [5, 10, 25, 50, 100],
                  sizesLabel: 'Show',
                  sizesDescription: 'per page',
                }}
                serverSide={true}
                aria-label="Berth conflicts table"
              />
            </div>
          )}
        </div>
      </div>
    </Container>
  );
};

export { BerthConflictListings };
